"use client"

import { cn } from "@/lib/utils"
import { getTagCategoryColor } from "@/lib/tag-categorizer"
import { trendingLabel, type TrendingTag } from "@/lib/trending-tags"
import { useTranslations } from "next-intl"

interface TrendingTagBadgesProps {
  tags: TrendingTag[]
  onPick: (tag: TrendingTag) => void
  className?: string
}

/**
 * Compact wrapped badges for trending tags, coloured by what the tag is (artist red, character green, series purple, the
 * same scheme as the tag pills). Used in the search bar's dropdown and in the Filters panel. The small number is how many of the
 * newest posts carry the tag (summed when it trends on several of the selected sources).
 */
export function TrendingTagBadges({ tags, onPick, className }: TrendingTagBadgesProps) {
  const t = useTranslations("trendingTags")
  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {tags.map((tag) => (
        <button
          key={tag.tag}
          type="button"
          // Keep the search box focused: a click on a badge must not blur it (which would close the panel before the click lands).
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onPick(tag)}
          title={t("badgeTitle", { count: tag.count })}
          className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border/40 bg-background/40 px-2.5 py-1 text-xs transition-colors hover:bg-primary/20 focus-visible:bg-primary/20 focus-visible:outline-none"
        >
          <span className={cn("truncate font-medium", getTagCategoryColor(tag.category))}>{trendingLabel(tag.tag)}</span>
          <span className="shrink-0 tabular-nums text-[10px] text-muted-foreground">{tag.count}</span>
        </button>
      ))}
    </div>
  )
}
